import type { SeoPageContentBlock } from './seo-page.models';

/**
 * Presentation-only layout for SEO page rich text, applied identically in the browser and the SEO
 * renderer. Strapi stores flat top-level HTML (h2/h3/p/ul/ol/blockquote); this marks the opening
 * paragraph as the lead and turns runs of two or more h3 sections into a panel grid. HTML that does
 * not split cleanly into top-level blocks is returned unchanged.
 */
const TOP_LEVEL_BLOCK = /<(h[1-6]|p|ul|ol|blockquote|figure|table|pre|div)\b[^>]*>[\s\S]*?<\/\1>|<hr\b[^>]*>/gi;
const HEADING = /^h[1-6]$/;
const NESTABLE = /^(ul|ol|blockquote|div|table)$/;

interface TopLevelBlock {
  tag: string;
  html: string;
}

function splitTopLevel(html: string): TopLevelBlock[] | null {
  const blocks: TopLevelBlock[] = [];
  let cursor = 0;
  for (const match of html.matchAll(TOP_LEVEL_BLOCK)) {
    if (html.slice(cursor, match.index).trim()) return null;
    const tag = (match[1] ?? 'hr').toLowerCase();
    // A lazy match cannot see nesting of the same tag (a list inside a list item).
    if (NESTABLE.test(tag) && new RegExp(`<${tag}\\b`, 'i').test(match[0].slice(1))) return null;
    blocks.push({ tag, html: match[0] });
    cursor = (match.index ?? 0) + match[0].length;
  }
  if (html.slice(cursor).trim()) return null;
  return blocks;
}

/** Consecutive h3 sections starting at `start`, each with at least one non-heading block. */
function collectPanels(blocks: TopLevelBlock[], start: number): { groups: string[][]; end: number } {
  const groups: string[][] = [];
  let cursor = start;
  while (cursor < blocks.length && blocks[cursor].tag === 'h3') {
    let end = cursor + 1;
    while (end < blocks.length && !HEADING.test(blocks[end].tag)) end++;
    if (end === cursor + 1) break;
    groups.push(blocks.slice(cursor, end).map((block) => block.html));
    cursor = end;
  }
  return { groups, end: cursor };
}

export function layoutSeoRichText(html: string, options: { lead: boolean }): string {
  const blocks = splitTopLevel(html);
  if (!blocks?.length) return html;

  const output: string[] = [];
  let leadOpen = options.lead;
  let index = 0;
  while (index < blocks.length) {
    const block = blocks[index];

    if (block.tag === 'h3') {
      leadOpen = false;
      const { groups, end } = collectPanels(blocks, index);
      if (groups.length >= 2) {
        const columns = groups.length % 3 === 0 ? 3 : 2;
        const panels = groups.map((group) => `<div class="seo-panel">${group.join('')}</div>`).join('');
        output.push(`<div class="seo-panels seo-panels--${columns}">${panels}</div>`);
        index = end;
        continue;
      }
    }

    if (leadOpen && block.tag === 'p' && /^<p(?![^>]*\bclass=)/i.test(block.html)) {
      output.push(block.html.replace(/^<p/i, '<p class="seo-lead"'));
      leadOpen = false;
    } else {
      if (!/^h[12]$/.test(block.tag)) leadOpen = false;
      output.push(block.html);
    }
    index++;
  }
  return output.join('\n');
}

/** The body gets a lead paragraph (first paragraph block only); the bottom body does not. */
export function layoutSeoBlocks(blocks: readonly SeoPageContentBlock[], options: { lead: boolean }): SeoPageContentBlock[] {
  return blocks.map((block, index) =>
    block.type === 'paragraph'
      ? { ...block, html: layoutSeoRichText(block.html, { lead: options.lead && index === 0 }) }
      : block,
  );
}
